import type { HttpClient } from '~/api/client';
import { httpClient } from '~/api/client';
import type {
  AcceptQuoteResponse as AcceptQuoteResponseModel,
  CreateQuoteResponse,
  MyQuoteRequestItem as MyQuoteRequestItemModel,
  MyQuoteRequestList,
  PartnerCardItem as PartnerCardItemModel,
  PartnerDetail,
  PartnerList,
  QuoteRequestDetail,
  QuoteRequestSummary,
  RecommendedPartnerList,
  ReceivedQuoteItem as ReceivedQuoteItemModel,
} from '~/api/model';
// Request/Response DTO 는 Zod Model 재사용 + 직접 정의 (OAISchema 금지 — api-typegen §0)

// ============================================================================
// Response 타입 (export for use in components)
// ============================================================================

/** 파트너 목록 응답 */
export type PartnerListResponse = PartnerList;

/** 추천 파트너 목록 응답 */
export type RecommendedPartnerListResponse = RecommendedPartnerList;

/** 파트너 상세 응답 */
export type PartnerDetailResponse = PartnerDetail;

/** 내 견적 요청 목록 응답 */
export type MyQuoteRequestListResponse = MyQuoteRequestList;

/** 견적 요청 상세 응답 */
export type QuoteRequestDetailResponse = QuoteRequestDetail;

/**
 * 파트너 카드 아이템
 * @property grade - 파트너 등급: PRIME_PLUS | PRIME | PRO | BASE
 * @property introduction - 소개글
 * @property location - 위치
 * @property officeName - 사무소명
 * @property partnerId - 파트너 ID
 * @property partnerName - 파트너명
 * @property serviceFee - 서비스 비용
 * @property thumbnailImageUrl - 썸네일 이미지 URL
 */
export type PartnerCardItem = PartnerCardItemModel;

/**
 * 내 견적 요청 아이템
 */
export type MyQuoteRequestItem = MyQuoteRequestItemModel;

/**
 * 받은 견적 아이템
 */
export type ReceivedQuoteItem = ReceivedQuoteItemModel;

/**
 * 견적 요청 생성 Request
 * @property annualRevenue - 연 매출 (필수)
 * @property businessAddress - 사업장 주소 (선택)
 * @property businessDetailAddress - 사업장 상세주소 (선택)
 * @property businessOwnerType - 사업자 유형: INDIVIDUAL_GENERAL(개인 일반) | INDIVIDUAL_SIMPLIFIED(개인 간이) | CORPORATION(법인) | OTHER(기타) (필수)
 * @property businessRegionCode - 사업장 지역 코드 (필수)
 * @property businessZipCode - 사업장 우편번호 (선택)
 * @property inquiryType - 문의 유형 (필수)
 * @property quoteType - 견적 유형: COMPARE(비교견적) | ONE_TO_ONE(1:1문의) | GRADE_BASED(등급기반) (필수)
 * @property requestContent - 요청 내용 (선택)
 * @property requesterIndustry - 요청자 업종 (필수)
 * @property requesterName - 요청자 이름 (필수)
 * @property requesterPhone - 요청자 연락처 (필수)
 * @property serviceType - 서비스 유형: TAX (필수)
 * @property targetPartnerIds - 대상 파트너 ID 목록 (필수)
 */
export type CreateQuoteRequestRequest = {
  annualRevenue: string;
  businessAddress?: string;
  businessDetailAddress?: string;
  businessOwnerType:
    | 'INDIVIDUAL_GENERAL'
    | 'INDIVIDUAL_SIMPLIFIED'
    | 'CORPORATION'
    | 'OTHER';
  businessRegionCode: string;
  businessZipCode?: string;
  grade?: 'PRIME_PLUS' | 'PRIME' | 'PRO' | 'BASE' | 'NONE';
  inquiryType:
    | 'BOOKKEEPING'
    | 'VAT_FILING'
    | 'INCOME_TAX'
    | 'TRANSFER_TAX'
    | 'INHERITANCE_GIFT_TAX'
    | 'TAX_EXEMPT_FILING'
    | 'OTHER';
  quoteType: 'COMPARE' | 'ONE_TO_ONE' | 'GRADE_BASED';
  requestContent?: string;
  requesterIndustry:
    | 'RESTAURANT'
    | 'TRADE'
    | 'PROFESSIONAL_SERVICE'
    | 'REAL_ESTATE_CONSTRUCTION'
    | 'ACCOMMODATION'
    | 'OTHER';
  requesterName: string;
  requesterPhone: string;
  serviceType: 'TAX';
  targetPartnerIds: number[];
};

/**
 * 견적 요청 생성 Response
 * @property message - 응답 메시지
 * @property quoteRequestId - 생성된 견적 요청 ID
 * @property quoteType - 견적 유형
 */
export type CreateQuoteRequestResponse = CreateQuoteResponse;

/**
 * 견적 수락 Response
 * @property quoteRequestStatus - 견적 요청 상태: WAITING | CONSULTING | CLOSED | EXPIRED
 * @property quoteResponseId - 수락한 견적 응답 ID
 */
export type AcceptQuoteResponse = AcceptQuoteResponseModel;

/**
 * 견적 요청 요약 Response
 * @property compareCount - 비교견적 요청 건수
 * @property gradeBasedCount - 등급 기반 견적 요청 건수
 * @property oneToOneCount - 1:1 견적 요청 건수
 * @property totalCount - 전체 견적 요청 건수
 */
export type QuoteRequestSummaryResponse = QuoteRequestSummary;

// ============================================================================
// Query Params 타입
// ============================================================================

/**
 * 파트너 목록 조회 파라미터
 * @property serviceType - 서비스 유형 (현재 TAX만 지원)
 * @property grade - 파트너 등급 필터: PRIME_PLUS | PRIME | PRO | BASE
 * @property lastId - 마지막 파트너 ID (커서 기반 페이지네이션)
 * @property size - 페이지 크기
 */
export type GetPartnersParams = {
  serviceType: 'TAX';
  grade?: 'PRIME_PLUS' | 'PRIME' | 'PRO' | 'BASE' | 'NONE';
  lastId?: number;
  size?: number;
};

/**
 * 추천 파트너 목록 조회 파라미터
 * @property serviceType - 서비스 유형 (현재 TAX만 지원)
 * @property regionCode - 지역 코드 (사업장 지역 기반 추천)
 * @property grade - 파트너 등급 필터: PRIME_PLUS | PRIME | PRO | BASE
 * @property lastId - 마지막 파트너 ID (커서 기반 페이지네이션)
 * @property size - 조회할 파트너 수
 */
export type GetRecommendedPartnersParams = {
  serviceType: 'TAX';
  regionCode: string;
  grade?: 'PRIME_PLUS' | 'PRIME' | 'PRO' | 'BASE' | 'NONE';
  lastId?: number;
  size?: number;
};

/**
 * 내 견적 요청 목록 조회 파라미터
 * @property serviceType - 서비스 유형 필터
 * @property lastId - 마지막 견적 요청 ID (커서 기반 페이지네이션)
 * @property size - 페이지 크기
 */
export type GetMyQuoteRequestsParams = {
  serviceType?: 'TAX';
  lastId?: number;
  size: number;
};

// ============================================================================
// DataSource Interface & Implementation
// ============================================================================

export interface PartnersDataSource {
  /** 파트너 목록 조회 */
  getPartners(params: GetPartnersParams): Promise<PartnerListResponse>;

  /** 추천 파트너 목록 조회 (지역 기반) */
  getRecommendedPartners(
    params: GetRecommendedPartnersParams,
  ): Promise<RecommendedPartnerListResponse>;

  /** 파트너 상세 조회 */
  getPartnerDetail(partnerId: number): Promise<PartnerDetailResponse>;

  /** 내 견적 요청 목록 조회 */
  getMyQuoteRequests(
    params: GetMyQuoteRequestsParams,
  ): Promise<MyQuoteRequestListResponse>;

  /** 견적 요청 상세 조회 */
  getQuoteRequestDetail(
    quoteRequestId: number,
  ): Promise<QuoteRequestDetailResponse>;

  /** 견적 요청 생성 */
  createQuoteRequest(
    request: CreateQuoteRequestRequest,
  ): Promise<CreateQuoteRequestResponse>;

  /** 견적 수락 */
  acceptQuote(quoteResponseId: number): Promise<AcceptQuoteResponse>;

  /** 견적 거절 */
  rejectQuote(quoteResponseId: number): Promise<void>;

  /** 견적 요청 요약 조회 */
  getQuoteRequestSummary(): Promise<QuoteRequestSummaryResponse>;
}

export class PartnersDataSourceImpl implements PartnersDataSource {
  // eslint-disable-next-line no-useless-constructor
  constructor(private readonly client: HttpClient = httpClient) {}

  async getPartners(params: GetPartnersParams) {
    return this.client.request<PartnerListResponse>({
      url: '/api/market-place/partners',
      method: 'get',
      params,
    });
  }

  async getRecommendedPartners(params: GetRecommendedPartnersParams) {
    return this.client.request<RecommendedPartnerListResponse>({
      url: '/api/market-place/partners/recommended',
      method: 'get',
      params,
    });
  }

  async getPartnerDetail(partnerId: number) {
    return this.client.request<PartnerDetailResponse>({
      url: `/api/market-place/partners/${partnerId}`,
      method: 'get',
    });
  }

  async getMyQuoteRequests(params: GetMyQuoteRequestsParams) {
    return this.client.request<MyQuoteRequestListResponse>({
      url: '/api/market-place/partners/my/quote-requests',
      method: 'get',
      params,
    });
  }

  async getQuoteRequestDetail(quoteRequestId: number) {
    return this.client.request<QuoteRequestDetailResponse>({
      url: `/api/market-place/partners/my/quote-requests/${quoteRequestId}`,
      method: 'get',
    });
  }

  async createQuoteRequest(request: CreateQuoteRequestRequest) {
    return this.client.request<CreateQuoteRequestResponse>({
      url: '/api/market-place/partners/quote-requests',
      method: 'post',
      data: request,
    });
  }

  async acceptQuote(quoteResponseId: number) {
    return this.client.request<AcceptQuoteResponse>({
      url: `/api/market-place/partners/my/quote-responses/${quoteResponseId}/accept`,
      method: 'post',
    });
  }

  async rejectQuote(quoteResponseId: number) {
    return this.client.request<void>({
      url: `/api/market-place/partners/my/quote-responses/${quoteResponseId}/reject`,
      method: 'post',
    });
  }

  async getQuoteRequestSummary() {
    return this.client.request<QuoteRequestSummaryResponse>({
      url: '/api/market-place/partners/quote-requests/summary',
      method: 'get',
    });
  }
}

export const partnersDataSource = new PartnersDataSourceImpl();
